import { TranslateService } from '@ngx-translate/core';
import { ConfigConstants } from '../../config/config-constants';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { ODataObject } from '../../interfaces/odata-model.interface';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { ModelOptionsLoaderService } from '../../services/model-options-loader/model-options-loader.service';
import { ValueHelperService } from '../../services/value-helper/value-helper.service';
import { CompositeModel } from '../complex/composite.model';
import { DropdownModel } from '../complex/dropdown.model';
import { RadioModel } from '../complex/radio.model';
import { RadioModelOptions } from '../options/radio-model-options';
import { MultilineTextModel, ODataMultilineTextOutModel } from '../primitive/multiline-text.model';

const odataPurposeOfTraining = 'PurposeOfTrainingSet';
const odataTrainingFocus = 'TrainingFocusSet';
const odataProductTrained = 'ProductTrainedSet';
const odataSystemSoftwareVersion = 'SystemSoftwareVersionSet';
const odataTraineesAttendedClassroom = 'TraineesAttendedClassroomSet';
const odataSystemOptionsReviewed = 'SystemOptionsReviewsSet';

export class ConfirmationEventSummaryModel extends CompositeModel {
    public primaryPurposeOfTraining: DropdownModel<MultilineTextModel>;
    public trainingFocus: DropdownModel<MultilineTextModel>;
    public otherTrainingFocus: MultilineTextModel;
    public primaryProductAndOrTopicTrained: MultilineTextModel;
    public systemSoftwareVersion: MultilineTextModel;
    public traineesAttendedClassroom: RadioModel<MultilineTextModel>;
    public systemOptionsReviewed: RadioModel<MultilineTextModel>;
    public constructor() {
        super('ConfirmationEventSummaryModel');
        const injector = CustomInjector.getInjector();
        const valueHelperService = injector.get(ValueHelperService);
        const translate = injector.get(TranslateService);
        const modelOptionsLoader = injector.get(ModelOptionsLoaderService);

        const purposeOfTrainingValues = valueHelperService.getPurposeOfTrainingValues();
        const purposeOfTrainingEntries = purposeOfTrainingValues.map(
            (vh) =>
                new DisplayDecorator<MultilineTextModel>(
                    vh.descText,
                    new MultilineTextModel(odataPurposeOfTraining, vh.descText),
                ),
        );
        this.primaryPurposeOfTraining = <DropdownModel<MultilineTextModel>>(
            this.set(new DropdownModel<MultilineTextModel>(odataPurposeOfTraining, purposeOfTrainingEntries))
        );
        modelOptionsLoader.load(this.primaryPurposeOfTraining, 'EventSummary', 'PrimaryPurpose');

        const trainingFocusValues = valueHelperService.getTrainingFocusValues();
        const trainingFocusEntries = trainingFocusValues.map(
            (vh) =>
                new DisplayDecorator<MultilineTextModel>(
                    vh.descText,
                    new MultilineTextModel(odataTrainingFocus, vh.descText),
                ),
        );
        this.trainingFocus = <DropdownModel<MultilineTextModel>>(
            this.set(new DropdownModel<MultilineTextModel>(odataTrainingFocus, trainingFocusEntries))
        );
        modelOptionsLoader.load(this.trainingFocus, 'EventSummary', 'TrainingFocus');

        this.otherTrainingFocus = new MultilineTextModel('OtherTrainingFocus');
        modelOptionsLoader.load(this.otherTrainingFocus, 'EventSummary', 'OtherTrainingFocus');

        this.primaryProductAndOrTopicTrained = <MultilineTextModel>(
            this.set(new MultilineTextModel(odataProductTrained))
        );
        modelOptionsLoader.load(this.primaryProductAndOrTopicTrained, 'EventSummary', 'PrimaryProductOrTopic');

        this.systemSoftwareVersion = <MultilineTextModel>this.set(new MultilineTextModel(odataSystemSoftwareVersion));
        modelOptionsLoader.load(this.systemSoftwareVersion, 'EventSummary', 'SystemSoftwareVersion');

        const traineesAttendedClassroomGroup = [
            new DisplayDecorator(
                <string>translate.instant('CONFIRMATION_EVENT_SUMMARY_OPTION_YES'),
                new MultilineTextModel(
                    ConfigConstants.optionYes,
                    <string>translate.instant('CONFIRMATION_EVENT_SUMMARY_OPTION_YES'),
                ),
            ),
            new DisplayDecorator(
                <string>translate.instant('CONFIRMATION_EVENT_SUMMARY_OPTION_NO'),
                new MultilineTextModel(
                    ConfigConstants.optionNo,
                    <string>translate.instant('CONFIRMATION_EVENT_SUMMARY_OPTION_NO'),
                ),
            ),
            new DisplayDecorator(
                <string>translate.instant('CONFIRMATION_EVENT_SUMMARY_OPTION_NONE'),
                new MultilineTextModel(
                    ConfigConstants.optionNA,
                    <string>translate.instant('CONFIRMATION_EVENT_SUMMARY_OPTION_NONE'),
                ),
            ),
        ];

        this.traineesAttendedClassroom = <RadioModel<MultilineTextModel>>(
            this.set(
                new RadioModel<MultilineTextModel>(
                    odataTraineesAttendedClassroom,
                    traineesAttendedClassroomGroup,
                    new RadioModelOptions({ initialSelection: 2 }),
                ),
            )
        );
        modelOptionsLoader.load(this.traineesAttendedClassroom, 'EventSummary', 'TraineesAttendedClassroom');

        const systemOptionsReviewedGroup = [
            new DisplayDecorator(
                <string>translate.instant('CONFIRMATION_EVENT_SUMMARY_OPTION_YES'),
                new MultilineTextModel(
                    ConfigConstants.optionYes,
                    <string>translate.instant('CONFIRMATION_EVENT_SUMMARY_OPTION_YES'),
                ),
            ),
            new DisplayDecorator(
                <string>translate.instant('CONFIRMATION_EVENT_SUMMARY_OPTION_NO'),
                new MultilineTextModel(
                    ConfigConstants.optionNo,
                    <string>translate.instant('CONFIRMATION_EVENT_SUMMARY_OPTION_NO'),
                ),
            ),
            new DisplayDecorator(
                <string>translate.instant('CONFIRMATION_EVENT_SUMMARY_OPTION_COVERED'),
                new MultilineTextModel(
                    ConfigConstants.confirmationEventSummaryOptionCovered,
                    <string>translate.instant('CONFIRMATION_EVENT_SUMMARY_OPTION_COVERED'),
                ),
            ),
            new DisplayDecorator(
                <string>translate.instant('CONFIRMATION_EVENT_SUMMARY_OPTION_NONE'),
                new MultilineTextModel(
                    ConfigConstants.optionNA,
                    <string>translate.instant('CONFIRMATION_EVENT_SUMMARY_OPTION_NONE'),
                ),
            ),
        ];

        this.systemOptionsReviewed = <RadioModel<MultilineTextModel>>(
            this.set(
                new RadioModel<MultilineTextModel>(
                    odataSystemOptionsReviewed,
                    systemOptionsReviewedGroup,
                    new RadioModelOptions({ initialSelection: 3 }),
                ),
            )
        );
        modelOptionsLoader.load(this.systemOptionsReviewed, 'EventSummary', 'SystemOptionsReviewed');
    }

    public toJSON(): unknown {
        return {
            primaryPurposeOfTraining: this.primaryPurposeOfTraining.selectedEntry
                ? this.primaryPurposeOfTraining.selectedEntry.value
                : [],
            trainingFocus: this.trainingFocus.selectedEntry ? this.trainingFocus.selectedEntry.value : [],
            otherTrainingFocus: this.otherTrainingFocus.value,
            primaryProductAndOrTopicTrained: this.primaryProductAndOrTopicTrained.value,
            systemSoftwareVersion: this.systemSoftwareVersion.value,
            traineesAttendedClassroom: this.traineesAttendedClassroom.selectedEntry.value,
            systemOptionsReviewed: this.systemOptionsReviewed.selectedEntry.value,
        };
    }

    public getOutModel(): ODataConfirmationEventSummaryOutModel {
        const trainingFocusValueOther =
            this.trainingFocus.selectedEntry &&
            this.trainingFocus.selectedEntry.value === ConfigConstants.trainingFocusOther;

        if (trainingFocusValueOther && this.otherTrainingFocus.value) {
            // MARS-121 Concatenate the value of the trainingFocus Dropdown with the value of the otherTrainingFOc
            this.trainingFocus.selectedEntry.value += ` - ${this.otherTrainingFocus.value}`;
        }

        const outModel = <ODataConfirmationEventSummaryOutModel>super.getOutModel();

        // after the outModel has been generated revert the value to other.
        if (trainingFocusValueOther && this.otherTrainingFocus.value) {
            this.trainingFocus.selectedEntry.value = ConfigConstants.trainingFocusOther;
        }

        return outModel;
    }
}

export class ConfirmationEventSummaryModelFromJson extends ConfirmationEventSummaryModel {
    public constructor(data: JsonConfirmationEventSummaryModel) {
        super();

        if (this.primaryPurposeOfTraining && data.primaryPurposeOfTraining) {
            this.primaryPurposeOfTraining.selectByDisplayName(data.primaryPurposeOfTraining);
        }

        if (this.trainingFocus && data.trainingFocus) {
            this.trainingFocus.selectByDisplayName(data.trainingFocus);
        }

        if (this.otherTrainingFocus && data.otherTrainingFocus) {
            this.otherTrainingFocus.value = data.otherTrainingFocus;
        }

        this.primaryProductAndOrTopicTrained.value = data.primaryProductAndOrTopicTrained;
        this.systemSoftwareVersion.value = data.systemSoftwareVersion;

        if (this.traineesAttendedClassroom && data.traineesAttendedClassroom) {
            this.traineesAttendedClassroom.selectByDisplayName(data.traineesAttendedClassroom);
        }

        if (this.systemOptionsReviewed && data.systemOptionsReviewed) {
            this.systemOptionsReviewed.selectByDisplayName(data.systemOptionsReviewed);
        }
    }
}

export interface JsonConfirmationEventSummaryModel {
    primaryPurposeOfTraining: string;
    trainingFocus: string;
    otherTrainingFocus: string;
    primaryProductAndOrTopicTrained: string;
    systemSoftwareVersion: string;
    traineesAttendedClassroom: string;
    systemOptionsReviewed: string;
}

export interface ODataConfirmationEventSummaryOutModel extends ODataObject {
    PurposeOfTrainingSet: ODataMultilineTextOutModel;
    TrainingFocusSet: ODataMultilineTextOutModel;
    ProductTrainedSet: ODataMultilineTextOutModel;
    SystemSoftwareVersionSet: ODataMultilineTextOutModel;
    TraineesAttendedClassroomSet: ODataMultilineTextOutModel;
    SystemOptionsReviewsSet: ODataMultilineTextOutModel;
}
